
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export const useAuth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isNewUser, setIsNewUser] = useState(false);

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['auth-session'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Access denied",
          description: "Please log in to view your training resources",
          variant: "destructive",
        });
        navigate("/");
        return null;
      }
      return session;
    },
  });

  useEffect(() => {
    if (session?.user) {
      // Improved check for whether a user needs to set a password
      // A user only needs to set a password if:
      // 1. They logged in with a magic link (provider='email')
      // 2. They don't have a password set (has_set_password is not true)
      // 3. They haven't seen the password prompt yet (has_seen_password_prompt is not true)
      // 4. They don't have a provider that indicates a social login or admin account
      
      const provider = session.user.app_metadata?.provider;
      const hasSetPassword = session.user.user_metadata?.has_set_password === true;
      const hasSeenPrompt = session.user.user_metadata?.has_seen_password_prompt === true;
      
      // Only prompt for new password if:
      // - User came from a magic link (provider is 'email')
      // - User hasn't set a password yet
      // - User hasn't dismissed the prompt before
      // - Provider isn't something else (like 'admin' or a social login)
      const needsPasswordPrompt = 
        provider === 'email' && 
        !hasSetPassword && 
        !hasSeenPrompt && 
        !session.user.app_metadata?.providers?.some((p: string) => p !== 'email');
      
      console.log('Password prompt check:', {
        provider,
        hasSetPassword,
        hasSeenPrompt,
        providers: session.user.app_metadata?.providers,
        needsPasswordPrompt
      });
      
      setIsNewUser(needsPasswordPrompt);
    }
  }, [session]);

  return {
    data: session,
    isLoading,
    error,
    isNewUser
  };
};
